import React, { useCallback, useMemo, useState } from 'react';
import { DataTable } from '@/components/data-table';
import { IconSearch } from '@tabler/icons-react';
import {
  FileStatus,
  FileWithValidation,
  FileStatistics,
  MetaValidationProgress,
  ProcessedFile,
  TableMode,
} from '../types';
import { useFileStatistics } from '../hooks/useFileStatistics';
import { createFileTableColumns } from './FileTableColumns';
import { filterFilesByStatus } from './fileTableFilterUtils';
import { useDuplicateControls } from './useDuplicateControls';
import { useFileTableToolbarSlots } from './FileTableToolbarSlots';

interface BulkFileTableProps {
  files: FileWithValidation[];
  metaValidationProgress?: MetaValidationProgress;
  onRemoveFile?: (file: FileWithValidation) => void;
  onRetryValidation?: (file: FileWithValidation) => void;
  onValidateAll?: () => void;
  onUploadFiles?: () => void;
  filesUploaded?: boolean;
  tableMode?: TableMode;
  statistics?: FileStatistics;
  uploadQueueState?: {
    files: Array<{
      file: File;
      status: 'pending' | 'uploading' | 'completed' | 'error' | 'cancelled';
      progress?: number;
      error?: string;
    }>;
  };
  actions?: React.ReactNode;
  customEmptyContent?: React.ReactNode;
  beforeSlot?: React.ReactNode;
  afterSlot?: React.ReactNode;
  canUpload?: boolean;
}

export const BulkFileTable: React.FC<BulkFileTableProps> = ({
  files,
  metaValidationProgress,
  onRemoveFile,
  onValidateAll,
  onUploadFiles,
  filesUploaded = false,
  tableMode = TableMode.DUPLICATE_CHECK,
  customEmptyContent,
  statistics,
  uploadQueueState,
  beforeSlot,
  afterSlot,
  canUpload = true,
}) => {
  const [statusFilter, setStatusFilter] = useState<FileStatus | 'all'>('all');
  const { showDuplicatesOf, onShowDuplicatesOf } = useDuplicateControls();

  // O(1) lookup map for upload queue status (avoids O(n) find per file)
  const uploadStatusMap = useMemo(() => {
    if (!uploadQueueState?.files?.length) return null;
    const map = new Map<
      string,
      (typeof uploadQueueState.files)[0]
    >();
    for (const qf of uploadQueueState.files) {
      map.set(
        `${qf.file.name}-${qf.file.size}-${qf.file.lastModified}`,
        qf
      );
    }
    return map;
  }, [uploadQueueState?.files]);

  const getUploadStatus = useCallback(
    (file: File) => {
      if (!uploadStatusMap) return null;

      const key = `${file.name}-${file.size}-${file.lastModified}`;
      const queuedFile = uploadStatusMap.get(key);

      return queuedFile
        ? {
            status: queuedFile.status,
            progress: queuedFile.progress || 0,
            error: queuedFile.error,
          }
        : null;
    },
    [uploadStatusMap]
  );

  // Get file statistics using our hook
  const calculatedStats = useFileStatistics({
    files,
    tableMode,
    uploadQueueState,
  });

  // Use statistics passed from parent or calculated ones as fallback
  const finalStatistics = statistics || calculatedStats.statistics;
  const duplicateFileNames =
    statistics?.duplicateFileNames || calculatedStats.duplicateFileNames;
  const hasValidationResults =
    statistics?.hasValidationResults ?? calculatedStats.hasValidationResults;
  const hasDuplicates =
    statistics?.hasDuplicates ?? calculatedStats.hasDuplicates;

  // Process files with duplicate detection and status mapping (reuse from useFileStatistics)
  const processedFiles = useMemo((): ProcessedFile[] => {
    const nameCount: Record<string, number> = {};

    // Count file names
    files.forEach(file => {
      const name = file.file.name;
      nameCount[name] = (nameCount[name] || 0) + 1;
    });

    return files.map((file): ProcessedFile => {
      const isDuplicate = nameCount[file.file.name] > 1;

      let processedStatus: FileStatus = 'ready-to-check';

      if (isDuplicate) {
        processedStatus = 'duplicated';
      } else {
        // Map validation status to file status
        switch (file.validationStatus.status) {
          case 'pending':
            processedStatus = 'ready-to-check';
            break;
          case 'server-valid':
            processedStatus = file.validationStatus.serverValidation?.exists
              ? 'exists'
              : 'allowed';
            break;
          case 'server-invalid':
          case 'invalid':
          case 'error':
            processedStatus = 'not-allowed';
            break;
          case 'validating':
          case 'server-validating':
            processedStatus = 'ready-to-check';
            break;
          default:
            processedStatus = 'ready-to-check';
        }

        // In upload progress mode, check upload queue status (O(1) via map)
        if (tableMode === TableMode.UPLOAD_PROGRESS && uploadStatusMap) {
          const key = `${file.file.name}-${file.file.size}-${file.file.lastModified}`;
          const queuedFile = uploadStatusMap.get(key);

          if (queuedFile) {
            switch (queuedFile.status) {
              case 'pending':
                processedStatus = 'pending';
                break;
              case 'uploading':
                processedStatus = 'uploading';
                break;
              case 'completed':
                processedStatus = 'uploaded';
                break;
              case 'error':
              case 'cancelled':
                processedStatus = 'failed';
                break;
            }
          } else if (processedStatus === 'not-allowed') {
            processedStatus = 'failed';
          } else if (
            processedStatus === 'allowed' ||
            processedStatus === 'exists'
          ) {
            processedStatus = 'pending';
          }
        }
      }

      return {
        ...file,
        processedStatus,
        duplicateGroup: isDuplicate ? file.file.name : undefined,
      };
    });
  }, [files, tableMode, uploadStatusMap]);

  // Filter files based on status and duplicate view
  const filteredFiles = useMemo(() => {
    return filterFilesByStatus(
      processedFiles,
      statusFilter,
      showDuplicatesOf,
      getUploadStatus
    );
  }, [processedFiles, statusFilter, showDuplicatesOf, getUploadStatus]);

  // Create column definitions
  const columns = useMemo(() => {
    return createFileTableColumns({
      tableMode,
      onRemoveFile: onRemoveFile
        ? (file: ProcessedFile) => onRemoveFile(file)
        : undefined,
      onShowDuplicatesOf,
      showDuplicatesOf,
      getUploadStatus,
    });
  }, [
    tableMode,
    onRemoveFile,
    onShowDuplicatesOf,
    showDuplicatesOf,
    getUploadStatus,
  ]);

  // Get toolbar slots using our hook
  const { leftSlot, centerSlot, rightSlot, bottomSlot } =
    useFileTableToolbarSlots({
      statistics: finalStatistics,
      tableMode,
      statusFilter,
      showDuplicatesOf,
      duplicateFileNames,
      duplicateFileStats:
        statistics?.duplicateFileStats ||
        calculatedStats?.duplicateFileStats ||
        {},
      metaValidationProgress,
      onStatusFilterChange: setStatusFilter,
      onShowDuplicatesOf,
      onValidateAll,
      onUploadFiles,
      filesUploaded,
      hasValidationResults,
      hasDuplicates,
      canUpload,
    });

  return (
    <>
      {/*<div className="bulk-file-table" style={{ display: 'contents' }}>*/}

      <DataTable
        data={filteredFiles}
        columns={columns}
        enableSorting={canUpload}
        enableFiltering={canUpload}
        enablePagination={canUpload}
        enableColumnVisibility={canUpload}
        enableRowSelection={false}
        virtualizationThreshold={50}
        searchPlaceholder="Search files..."
        pageSizeOptions={[10, 25, 50, 100]}
        toolbarLeftSlot={canUpload ? leftSlot : null}
        toolbarCenterSlot={canUpload ? centerSlot : null}
        toolbarRightSlot={rightSlot}
        toolbarBottomSlot={canUpload ? bottomSlot : null}
        emptyMessage="No files match your criteria"
        emptyIcon={<IconSearch />}
        customEmptyContent={customEmptyContent}
        getRowId={(row: ProcessedFile) => row.validationStatus.id}
        className="bulk-file-table__data-table"
        beforeSlot={beforeSlot}
        afterSlot={afterSlot}
      />
      {/*</div>*/}
    </>
  );
};
